import type { CompanySearchResult } from '@dash-lite/sdk';
import { useEffect, useRef, useState } from 'react';
import { dashlite } from '@/lib/dashlite';
import { presentError } from '@/lib/errors';
import type { ErrorPresentation } from '@/lib/errors';

/**
 * Aperçu public d'une entreprise, à partir de son seul identifiant.
 *
 * C'est le pendant gratuit de la fiche complète : `GET /companies/search` est
 * **public** — ni compte, ni droit d'accès — et renvoie déjà de quoi montrer
 * au visiteur qu'il est au bon endroit (dénomination, identifiant, ville,
 * forme juridique, pays). Tout le reste — capital, activité, dirigeants,
 * effectifs, greffe — n'arrive qu'avec `GET /companies/{id}`, derrière le
 * droit d'accès.
 *
 * Cette séparation est ce qui permet d'afficher la page de paiement **avec**
 * l'entreprise concernée sous les yeux, sans livrer la moindre donnée payante
 * avant le paiement.
 *
 * Deux chemins d'arrivée, donc deux comportements :
 *
 * - depuis la liste de résultats, le résultat est transporté dans l'état de
 *   navigation : il s'affiche immédiatement, sans appel supplémentaire ;
 * - sur une URL ouverte directement (lien partagé, rafraîchissement), l'état
 *   de navigation est vide : la recherche est rejouée sur l'identifiant.
 */

export type CompanyPreviewState = {
    company: CompanySearchResult | null;
    loading: boolean;
    error: ErrorPresentation | null;
    /** `true` quand la recherche a abouti sans reconnaître l'identifiant. */
    unknown: boolean;
};

/** Résultat d'une recherche, accompagné de l'identifiant qui l'a produit. */
type PreviewOutcome = {
    registrationNumber: string;
    company: CompanySearchResult | null;
    error: ErrorPresentation | null;
};

/**
 * Deux écritures d'un même identifiant désignent la même entreprise :
 * « CHE-111.222.333 » et « CHE111222333 » ne doivent pas être comparées
 * caractère à caractère.
 */
function isSameNumber(left: string, right: string): boolean {
    const normalize = (value: string): string => value.replace(/[\s./-]/g, '').toUpperCase();

    return normalize(left) === normalize(right);
}

export function useCompanyPreview(
    registrationNumber: string | null,
    /** Résultat déjà connu, transporté depuis la liste de recherche. */
    initial: CompanySearchResult | null = null,
): CompanyPreviewState {
    /** L'aperçu transporté ne vaut que s'il porte sur l'entreprise affichée. */
    const carried =
        registrationNumber !== null && initial !== null && isSameNumber(initial.registration_number, registrationNumber)
            ? initial
            : null;

    /**
     * Le résultat de la recherche porte l'identifiant qui l'a produit :
     * l'état affiché se déduit alors du rendu, sans jamais poser d'état
     * intermédiaire « en chargement » depuis l'effet.
     */
    const [outcome, setOutcome] = useState<PreviewOutcome | null>(null);

    /**
     * Identifiant déjà demandé : neutralise le double montage de `StrictMode`
     * en développement, qui enverrait sinon deux recherches identiques.
     *
     * C'est volontairement une garde **et pas** un drapeau d'annulation posé
     * dans le nettoyage de l'effet : les deux ensemble feraient ignorer la
     * réponse du seul appel réellement émis.
     */
    const requestedNumber = useRef<string | null>(null);

    useEffect(() => {
        if (registrationNumber === null || carried !== null) {
            return;
        }

        if (requestedNumber.current === registrationNumber) {
            return;
        }

        requestedNumber.current = registrationNumber;

        dashlite
            .searchCompanies(registrationNumber)
            .then((results) => {
                // L'identifiant recherché est exact : on retient la
                // correspondance stricte, et à défaut le premier résultat —
                // certains registres répondent avec une écriture formatée.
                const match =
                    results.find((result) => isSameNumber(result.registration_number, registrationNumber)) ??
                    results[0] ??
                    null;

                setOutcome({ registrationNumber, company: match, error: null });
            })
            .catch((caught: unknown) => {
                // Une recherche en panne ne doit pas empêcher de payer :
                // l'erreur est rapportée, l'écran de paiement reste utilisable.
                setOutcome({ registrationNumber, company: null, error: presentError(caught) });
            });
    }, [carried, registrationNumber]);

    if (registrationNumber === null) {
        return { company: null, loading: false, error: null, unknown: false };
    }

    if (carried !== null) {
        return { company: carried, loading: false, error: null, unknown: false };
    }

    // Une réponse qui porte sur un autre identifiant est ignorée : c'est le
    // cas quand l'entreprise change sans que la page soit remontée.
    const resolved = outcome !== null && outcome.registrationNumber === registrationNumber ? outcome : null;

    return {
        company: resolved?.company ?? null,
        loading: resolved === null,
        error: resolved?.error ?? null,
        unknown: resolved !== null && resolved.company === null && resolved.error === null,
    };
}
